import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { useAppDispatch } from '@/data/store/utils/redux';
import { createOrganizationInvitationAsync } from '@/data/store/actions';
import { TOrganizationInvitationPayload, TRoleItem } from '@/data/types';
import { EmailStep, RoleStep, SuccessStep } from './components';

type InviteStep = 'email' | 'role' | 'success';

const PersonnelInvite: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const [currentStep, setCurrentStep] = useState<InviteStep>('email');
  const [email, setEmail] = useState('');
  const [selectedRole, setSelectedRole] = useState<TRoleItem | null>(null);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEmailNext = useCallback(async (emailValue: string) => {
    setIsLoading(true);
    setError(null);
    setEmail(emailValue);

    try {
      // Email validation is already handled in EmailStep component
      // You can add additional server-side validation here if needed
      
      // Auto-advance to role step
      setTimeout(() => {
        setIsLoading(false);
        setCurrentStep('role');
      }, 800); // Reduced delay for better UX
      
    } catch (error) {
      setIsLoading(false);
      setError('Σφάλμα κατά την επαλήθευση του email');
    }
  }, []);

  const handleRoleBack = useCallback(() => {
    setCurrentStep('email');
  }, []);

  const handleInviteComplete = useCallback(async (
    role: TRoleItem | null, 
    permissions: string[]
  ) => {
    setIsLoading(true);
    setError(null);
    setSelectedRole(role);
    setSelectedPermissions(permissions);

    try {
      // Create invitation payload matching original structure
      const payload: TOrganizationInvitationPayload = {
        email: email,
        roles: role ? [role.id] : [], // Single role selection
        departments: [], // Empty as in original
        countries: ["GR"], // Fixed to Greece as in original
        permissions: role ? [] : permissions, // Use role permissions or custom permissions
        permission_sets: [], // Empty as in original
      };

      await dispatch(createOrganizationInvitationAsync({ payload }));
      setCurrentStep('success');
      
    } catch (error) {
      console.error('Failed to send invite:', error);
      setError('Σφάλμα κατά την αποστολή της πρόσκλησης. Παρακαλώ δοκιμάστε ξανά.');
    } finally {
      setIsLoading(false);
    }
  }, [email, dispatch]);

  const handleBackToPersonnel = useCallback(() => {
    navigate('/personnel');
  }, [navigate]);

  const handleSendAnother = useCallback(() => {
    setCurrentStep('email');
    setEmail('');
    setSelectedRole(null);
    setSelectedPermissions([]);
    setError(null);
  }, []);

  const stepComponents = {
    email: (
      <EmailStep 
        onNext={handleEmailNext} 
        isLoading={isLoading}
        error={error}
      />
    ),
    role: (
      <RoleStep 
        email={email} 
        onBack={handleRoleBack} 
        onComplete={handleInviteComplete} 
        isLoading={isLoading}
        error={error}
      />
    ),
    success: (
      <SuccessStep
        email={email}
        selectedRole={selectedRole}
        selectedPermissions={selectedPermissions}
        onSendAnother={handleSendAnother}
        onBackToPersonnel={handleBackToPersonnel}
      />
    ),
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50/50 via-white to-blue-50/30 p-6">
      <div className="mx-auto max-w-7xl relative">
        {/* Header with back button */}
        <div className="mb-8">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleBackToPersonnel}
            className="mb-6 flex items-center gap-2 text-gray-600 transition-colors hover:text-gray-800"
          >
            <ArrowLeft className="h-5 w-5" />
            Πίσω στο Προσωπικό
          </motion.button>

          {/* Progress indicator */}
          <div className="mb-20 flex items-center justify-center">
            <div className="flex items-center gap-4">
              {(['email', 'role', 'success'] as InviteStep[]).map((step, index) => {
                const stepNames = {
                  email: 'Email',
                  role: 'Ρόλος',
                  success: 'Ολοκλήρωση',
                };

                const isActive = currentStep === step;
                const isCompleted = ['email', 'role', 'success'].indexOf(currentStep) > index;

                return (
                  <React.Fragment key={step}>
                    <div className="flex items-center gap-2">
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                          isActive
                            ? 'bg-blue-600 text-white'
                            : isCompleted
                              ? 'bg-green-600 text-white'
                              : 'bg-gray-200 text-gray-600'
                        }`}
                      >
                        {isCompleted ? <CheckCircle className="h-4 w-4" /> : index + 1}
                      </div>
                      <span
                        className={`text-sm font-medium ${
                          isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                        }`}
                      >
                        {stepNames[step]}
                      </span>
                    </div>
                    {index < 2 && <div className={`h-px w-12 ${isCompleted ? 'bg-green-600' : 'bg-gray-300'}`} />}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        {/* Step content */}
        <AnimatePresence mode="wait">
          <motion.div key={currentStep}>
            {stepComponents[currentStep]}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PersonnelInvite;